/**
 * Public Chat Page: the app's only entry page (/welcome).
 *
 * Every way into the app lands here (entry.ts): ads, search, the website, /landing/signup and
 * /landing/signin, logging out, an expired session. On top the message of the ad the visitor saw,
 * right below it the chat that argues "why register at all", beside it the account panel with
 * "Registrieren" and "Anmelden". On a phone the panel opens as an overlay.
 *
 * Measured (web_events, section = entry): chat_view, chat_message, chat_card, auth_open and the
 * register_* / login_* steps of the forms (use-public-auth.ts).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Link, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';
import { readAttributionFromBrowser } from 'features/engagement/signup/attribution';
import { PublicTopBar } from './components/PublicTopBar';
import { PublicChatContainer } from './components/PublicChatContainer';
import { AuthPanel, TrustLines } from './components/AuthPanel';
import { SignupCard } from './cards/SignupCard';
import { LoginCard } from './cards/LoginCard';
import { usePublicChat } from './hooks/use-public-chat';
import { displayableTerm, resolveEntry, type AuthTab } from './entry';
import { EntryProvider, trackEntry, type EntryContextValue } from './entry-context';

const CARD_FIELD: Record<string, string> = {
  SHOW_SIGNUP: 'signup',
  SHOW_LOGIN: 'login',
  SHOW_PRICING: 'pricing',
  SHOW_SAVINGS: 'savings',
  SHOW_SAFETY: 'safety',
  SHOW_FEATURES: 'features',
};

export const PublicChatPage: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read once on arrival: the ad origin and which version of the page to show.
  const [attribution] = useState(() => readAttributionFromBrowser(location.search));
  const [{ entry, tab: initialTab }] = useState(() => resolveEntry(location.search, attribution));
  const [sessionExpired, setSessionExpired] = useState(() => searchParams.get('session_expired') === 'true');
  const googleNoAccount = Boolean((location.state as { googleNoAccount?: boolean } | null)?.googleNoAccount);
  const [tab, setTab] = useState<AuthTab>(googleNoAccount ? 'signup' : initialTab);
  // Phone: the account panel is an overlay. It opens by itself only to sign in (or after a Google
  // sign-in without an account); a sign-up link shows the chat first, with the sticky button.
  const [overlayOpen, setOverlayOpen] = useState(() => initialTab === 'login' || googleNoAccount);
  const term = entry === 'suche' ? displayableTerm(attribution?.utm_term) : undefined;

  const base = useMemo(() => ({ entry, attribution }), [entry, attribution]);
  const cardsSeen = useRef(new Set<string>());

  const chat = usePublicChat({
    entry,
    attribution,
    onCards: (cards) => {
      for (const card of cards) {
        if (cardsSeen.current.has(card)) continue;
        cardsSeen.current.add(card);
        trackEntry(base, 'chat_card', CARD_FIELD[card] ?? 'other');
      }
    },
  });

  useEffect(() => {
    trackEntry(base, 'chat_view', undefined, { cta: initialTab });
    if (sessionExpired) {
      // Shown once; the parameter stays out of a reload.
      searchParams.delete('session_expired');
      setSearchParams(searchParams, { replace: true });
    }
    // Once per page view.
  }, []);

  const openAuth = useCallback(
    (next: AuthTab, cta: string) => {
      setTab(next);
      if (isPhone) setOverlayOpen(true);
      trackEntry(base, 'auth_open', next, { cta });
    },
    [base, isPhone]
  );

  const userMessages = chat.messages.filter((m) => m.role === 'user').length;
  const ask = (content: string, chip: boolean) => {
    trackEntry(base, 'chat_message', chip ? 'chip' : 'typed', { depth: userMessages + 1 });
    void chat.sendMessage(content, { chip });
  };

  const ctx: EntryContextValue = { entry, attribution, sessionId: chat.sessionId, tab, openAuth, googleNoAccount };
  const kick = term ? t('entry.suche.kickTerm', { term }) : t(`entry.${entry}.kick`);

  return (
    <EntryProvider value={ctx}>
      <Box className={classes.page}>
        <PublicTopBar onLoginClick={() => openAuth('login', 'topbar')} onSignupClick={() => openAuth('signup', 'topbar')} />

        <Box className={classes.content}>
          <Box className={classes.hero}>
            <Typography className={classes.kick}>{kick}</Typography>
            <Typography component="h1" className={classes.h1}>
              {t(`entry.${entry}.h1a`)}
              <span className={classes.h1em}>{t(`entry.${entry}.h1em`)}</span>
              {t(`entry.${entry}.h1b`)}
            </Typography>
            <Typography className={classes.sub}>{t('entry.sub')}</Typography>
          </Box>

          <Box className={classes.row}>
            <Box className={classes.chatColumn}>
              <PublicChatContainer chat={chat} onAsk={ask} compact={isPhone} />
            </Box>
            {!isPhone && (
              <Box className={classes.railColumn}>
                <AuthPanel />
              </Box>
            )}
          </Box>

          {isPhone && (
            <Box className={classes.phoneTrust}>
              <TrustLines />
            </Box>
          )}

          <Box className={classes.footer}>
            <span>{t('entry.footerServer')}</span>
            <Link href="https://secret-ai-agent.de/impressum" target="_blank" rel="noopener">
              {t('entry.imprint')}
            </Link>
            <Link href="https://secret-ai-agent.de/datenschutz" target="_blank" rel="noopener">
              {t('entry.privacy')}
            </Link>
            <Link href="/dashboard/legal/agb.html" target="_blank" rel="noopener">
              {t('entry.terms')}
            </Link>
          </Box>
        </Box>

        {isPhone && !overlayOpen && (
          <Box className={classes.sticky}>
            <Typography className={classes.stickyLine}>{t('entry.stickyLine')}</Typography>
            <Button variant="contained" className={classes.stickyButton} onClick={() => openAuth('signup', 'sticky')}>
              {t('entry.stickyCta')}
            </Button>
          </Box>
        )}

        {isPhone && overlayOpen && (
          <Box className={classes.overlay} role="dialog" aria-modal="true">
            <Box className={classes.overlayCard}>
              {tab === 'signup' ? (
                <SignupCard
                  place="overlay"
                  trackView
                  initiallyExpanded={false}
                  title={t('entry.inlineSignupTitle')}
                  onClose={() => setOverlayOpen(false)}
                  onSwitchToLogin={() => openAuth('login', 'overlay_switch')}
                />
              ) : (
                <LoginCard
                  place="overlay"
                  title={t('entry.railLoginTitle')}
                  onClose={() => setOverlayOpen(false)}
                  onSwitchToSignup={() => openAuth('signup', 'overlay_switch')}
                />
              )}
            </Box>
          </Box>
        )}

        <Snackbar
          open={sessionExpired}
          autoHideDuration={8000}
          onClose={() => setSessionExpired(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ top: 80 }}
        >
          <Alert severity="info" variant="filled" onClose={() => setSessionExpired(false)}>
            {t('entry.sessionExpired')}
          </Alert>
        </Snackbar>
      </Box>
    </EntryProvider>
  );
};

const useStyles = makeStyles()((theme) => ({
  page: {
    minHeight: '100vh',
    backgroundColor: theme.palette.backgrounds.main_new,
  },
  content: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: theme.spacing(13, 3, 5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3.5),
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(10, 1.5, 14),
      gap: theme.spacing(2),
    },
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.25),
    [theme.breakpoints.down('md')]: {
      alignItems: 'flex-start',
      textAlign: 'left',
      padding: theme.spacing(0, 0.5),
    },
  },
  kick: {
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
  },
  h1: {
    margin: 0,
    fontSize: '2.75rem',
    lineHeight: 1.1,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    maxWidth: 900,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.75rem',
    },
  },
  h1em: {
    color: theme.palette.primary.main,
  },
  sub: {
    fontSize: '1.1rem',
    lineHeight: 1.5,
    maxWidth: 680,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('md')]: {
      fontSize: '0.95rem',
    },
  },
  row: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'flex-start',
  },
  chatColumn: {
    flex: 1,
    minWidth: 0,
  },
  railColumn: {
    flex: '0 0 360px',
    position: 'sticky',
    top: 88,
  },
  phoneTrust: {
    padding: theme.spacing(0, 1),
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: theme.spacing(1, 2.5),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    '& a': {
      color: theme.palette.text.secondary,
    },
  },
  sticky: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 900,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.greys.lighter}`,
  },
  stickyLine: {
    flex: 1,
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  stickyButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    padding: theme.spacing(1.25, 2.25),
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1100,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    overflowY: 'auto',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  overlayCard: {
    width: '100%',
    maxWidth: 460,
    margin: 'auto 0',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    boxSizing: 'border-box',
  },
}));

export default PublicChatPage;
